import { EventEmitter, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import * as fromActions from './slice/payments.actions';
import * as fromSelectors from './slice/payments.selectors';
import { appStartedByDeepLinkClick } from '~/modules/app/slice';
import { selectIsAccessAppAvailable } from '@qvs/core/auth';
import { catchError, filter, map, switchMap, take, takeUntil, tap, withLatestFrom } from 'rxjs/operators';
import { select, Store } from '@ngrx/store';
import { PaymentService } from './payment.service';
import { EMPTY, forkJoin, of, ReplaySubject, Subject, throwError, timer, zip } from 'rxjs';
import {
  OrdersPaymentRequest,
  OrdersPaymentRequestStatusEnum
} from '~/lazy/payments/pages/new-payment/models/ordersPaymentRequest';
import { PaymentMapper } from '~/modules/payments/payment.mapper';
import {
  automaticSubmitPaymentOrderAfterMilliseconds,
  updatePaymentStatusIntervalInMilliseconds
} from '~/lazy/payments/models/payment.settings';
import { PaymentStatusEnum } from '@qvs/core';
import { environment } from '#environment';
import { ModalControllerService } from '~/shared/services/modal-controller.service';
import { ValidatePaymentOrderRequestPage } from '~/lazy/payments/pages/validate-payment-order-request/validate-payment-order-request.page';
import { Router } from '@angular/router';
import * as accountActions from '~/modules/accounts/slice';
import { selectAccountsThatCanCreatePayments } from '~/modules/accounts/slice';
import { loadAccountsSuccess } from '~/modules/accounts/slice/account.actions';
import { isEmpty, isNil } from 'lodash-es';

@Injectable()
export class PaymentsEffects {
  readonly destroyed$ = new Subject<void>();

  loadRecentPayments$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.loadPaymentRecentPayments),
      withLatestFrom(this.store.select(fromSelectors.selectRecentPayments)),
      switchMap(([action, recentPayments]) => {
        if (action.loadOnlyIfEmpty && recentPayments?.length > 0) {
          return of(fromActions.loadPaymentRecentPaymentsSuccess({ payments: null }));
        }
        return this.paymentService.loadRecentPayments().pipe(
          map((payments) => fromActions.loadPaymentRecentPaymentsSuccess({ payments })),
          catchError(() => of(fromActions.loadPaymentRecentPaymentsFailure()))
        );
      })
    )
  );

  LoadPaymentIfNotLoadedYet$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.loadPaymentIfNotLoadedYet),
      // a possible extension point for future: we could also check if the payment is already being loaded and avoid
      // dispatching the action again. This would require a LoadingStatus mapped to a paymentId in the store.
      concatLatestFrom((action) => this.store.pipe(select(fromSelectors.selectPaymentById(action.paymentId)))),
      switchMap(([{ paymentId }, payment]) => {
        if (payment) {
          return EMPTY;
        }
        return of(fromActions.loadPayment({ paymentId }));
      })
    )
  );

  loadPayment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.loadPayment),
      switchMap((action) =>
        this.paymentService.loadPayment(action.paymentId).pipe(
          map((payment) => fromActions.loadPaymentSuccess({ payment })),
          catchError(() => of(fromActions.loadPaymentFailure()))
        )
      )
    )
  );

  loadRecentTransfers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.loadPaymentRecentTransfers),
      switchMap(() =>
        this.paymentService.loadRecentTransfers().pipe(
          map((transfers) => fromActions.loadPaymentRecentTransfersSuccess({ transfers })),
          catchError(() => of(fromActions.loadPaymentRecentTransfersFailure()))
        )
      )
    )
  );

  loadCategories$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.loadPaymentCategories),
      switchMap(() =>
        this.paymentService.loadCategories().pipe(
          map((categories) => fromActions.loadPaymentCategoriesSuccess({ categories })),
          catchError(() => of(fromActions.loadPaymentCategoriesFailure()))
        )
      )
    )
  );

  loadSwiftCodes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.loadPaymentSwiftCodes),
      switchMap((action) =>
        this.paymentService.loadSwiftCodes(action.country).pipe(
          map((swiftCodes) => fromActions.loadPaymentSwiftCodesSuccess({ swiftCodes })),
          catchError(() => of(fromActions.loadPaymentSwiftCodesFailure({ country: action.country })))
        )
      )
    )
  );

  loadSwiftCodeFromIban$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.loadSwiftCodeFromIban),
      switchMap((action) =>
        this.paymentService.loadSwiftCodeFromIban(action.iban).pipe(
          map((swiftCode) => fromActions.loadSwiftCodeFromIbanSuccess({ swiftCode, iban: action.iban })),
          catchError(() => of(fromActions.loadSwiftCodeFromIbanFailure()))
        )
      )
    )
  );

  loadSwiftCodesForAccounts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadAccountsSuccess),
      concatLatestFrom(() => this.store.pipe(select(selectAccountsThatCanCreatePayments))),
      filter(([_, accounts]) => !isEmpty(accounts)),
      switchMap(([_, accounts]) => {
        const accountWithIban = accounts.find((account) => !isNil(account.ibanIdentification));
        if (!accountWithIban?.ibanIdentification) {
          return throwError(() => new Error('No account with IBAN found'));
        }
        const iban = accountWithIban.ibanIdentification;
        return forkJoin([this.paymentService.loadSwiftCodeFromIban(iban), of(accounts)]);
      }),
      switchMap(([swiftInfo, accounts]) => {
        const updatedAccounts = accounts.map((account) => ({
          ...account,
          swiftBicCode: swiftInfo?.code,
          bank: swiftInfo?.bank,
          branchAddress: swiftInfo?.branchAddress,
          country: swiftInfo?.country
        }));
        return of(
          accountActions.upsertAccounts({ entities: updatedAccounts }),
          fromActions.loadSwiftCodesForAccountsSuccess({ updatedAccounts })
        );
      }),
      catchError((error) => of(fromActions.loadSwiftCodesForAccountsFailure({ error })))
    )
  );

  createOrdersPaymentAskForExecutionSuccess = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.createOrdersPaymentAskForExecutionSuccess, fromActions.createOrdersPaymentCanceled),
      switchMap((action) => {
        if (action.type === fromActions.createOrdersPaymentCanceled.type) {
          return EMPTY;
        }
        return timer(automaticSubmitPaymentOrderAfterMilliseconds).pipe(
          switchMap(() =>
            of(
              fromActions.createOrdersPayment({
                ordersPaymentRequest: { ...action.ordersPaymentRequest, status: OrdersPaymentRequestStatusEnum.userSubmit }
              })
            )
          )
        );
      })
    )
  );

  createOrdersPaymentSuccess = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.createOrdersPaymentSuccess),
      switchMap((action) =>
        of(
          fromActions.authorizeOrdersPayment({
            ordersPaymentRequest: { ...action.ordersPaymentRequest, status: OrdersPaymentRequestStatusEnum.userSubmitSuccess }
          })
        )
      )
    )
  );

  authorizeOrdersPaymentSuccess = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.authorizeOrdersPaymentSuccess),
      switchMap((action) =>
        of(
          fromActions.confirmAuthorizedOrdersPayment({
            ordersPaymentRequest: { ...action.ordersPaymentRequest, status: OrdersPaymentRequestStatusEnum.userSubmitSuccess }
          })
        )
      )
    )
  );

  updatePaymentOrdersRequests = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.updatePaymentOrdersRequests),
      switchMap((action) => timer(updatePaymentStatusIntervalInMilliseconds).pipe(switchMap(() => of(action)))),
      withLatestFrom(this.store.pipe(select(fromSelectors.selectOrdersPaymentRequests))),
      switchMap(([action, existingRequests]) => {
        const requestsAlreadyExists = new Map<string, OrdersPaymentRequest>(
          Array.from(action.requests.entries()).filter(
            ([uuid]) =>
              existingRequests &&
              existingRequests.has(uuid) &&
              !PaymentMapper.isOrdersPaymentRequestSuccessfullyRegistered(existingRequests.get(uuid)) &&
              existingRequests.get(uuid).status !== PaymentStatusEnum.UNREGISTERED_TIMED_OUT &&
              existingRequests.get(uuid).status !== PaymentStatusEnum.DECLINED &&
              existingRequests.get(uuid).status !== OrdersPaymentRequestStatusEnum.userMaxStatusUpdateFailure
          )
        );

        if (requestsAlreadyExists.size === 0) {
          return EMPTY;
        }

        return zip(
          ...Array.from(requestsAlreadyExists.values()).map((request) => {
            if (request.payment) {
              return this.paymentService.loadPaymentOrdersRequest(request);
            }
            if (request.transfer) {
              return this.paymentService.loadPaymentInternalTransferRequest(request);
            }
            return throwError(() => new Error(this.unknownPaymentType));
          })
        ).pipe(
          switchMap((requests) =>
            of(
              fromActions.updatePaymentOrdersRequestsSuccess({
                requests: new Map(requests.map((request) => [request.uuid, request]))
              })
            )
          ),
          catchError(() => {
            this.navigateToPayments();
            return of(fromActions.updatePaymentOrdersRequestsFailure({ requests: action.requests }));
          })
        );
      })
    )
  );

  createOrdersPayment = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.createOrdersPayment),
      switchMap((action) =>
        timer(100).pipe(
          switchMap(() =>
            this.store.select(fromSelectors.selectOrdersPaymentRequests).pipe(
              take(1),
              switchMap((ordersPaymentRequests) => {
                if (ordersPaymentRequests?.has(action?.ordersPaymentRequest?.uuid)) {
                  const request = ordersPaymentRequests.get(action.ordersPaymentRequest.uuid);

                  if (request.payment) {
                    return this.paymentService.createOrderPayment(request).pipe(
                      switchMap((paymentCreationResponse) =>
                        of(
                          fromActions.createOrdersPaymentSuccess({
                            ordersPaymentRequest: { ...action.ordersPaymentRequest, paymentCreationResponse }
                          })
                        )
                      ),
                      catchError(() => {
                        this.navigateToPayments();
                        return of(
                          fromActions.createOrdersPaymentFailure({
                            ordersPaymentRequest: {
                              ...action.ordersPaymentRequest,
                              status: OrdersPaymentRequestStatusEnum.userSubmitFailure
                            }
                          })
                        );
                      })
                    );
                  }
                  if (request.transfer) {
                    return this.paymentService.createOrderInternalTransfer(request).pipe(
                      switchMap((paymentCreationResponse) =>
                        of(
                          fromActions.createOrdersPaymentSuccess({
                            ordersPaymentRequest: { ...action.ordersPaymentRequest, paymentCreationResponse }
                          })
                        )
                      ),
                      catchError(() => {
                        this.navigateToPayments();
                        return of(
                          fromActions.createOrdersPaymentFailure({
                            ordersPaymentRequest: {
                              ...action.ordersPaymentRequest,
                              status: OrdersPaymentRequestStatusEnum.userSubmitFailure
                            }
                          })
                        );
                      })
                    );
                  }
                  return throwError(() => new Error(this.unknownPaymentType));
                } else {
                  return of(
                    fromActions.createOrdersPaymentFailure({
                      ordersPaymentRequest: {
                        ...action.ordersPaymentRequest,
                        status: OrdersPaymentRequestStatusEnum.userSubmitFailure
                      }
                    })
                  );
                }
              })
            )
          )
        )
      )
    )
  );

  authorizeOrdersPayment = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.authorizeOrdersPayment),
      switchMap((action) =>
        this.store.select(fromSelectors.selectOrdersPaymentRequests).pipe(
          take(1),
          switchMap((ordersPaymentRequests) => {
            if (ordersPaymentRequests?.has(action?.ordersPaymentRequest?.uuid)) {
              const request = ordersPaymentRequests.get(action.ordersPaymentRequest.uuid);
              if (request.payment) {
                return this.paymentService.authorizeOrderPayment(request).pipe(
                  switchMap((paymentAuthorizationResponse) =>
                    of(
                      fromActions.authorizeOrdersPaymentSuccess({
                        ordersPaymentRequest: { ...action.ordersPaymentRequest, paymentAuthorizationResponse }
                      })
                    )
                  ),
                  catchError(() => {
                    this.navigateToPayments();
                    return of(
                      fromActions.authorizeOrdersPaymentFailure({
                        ordersPaymentRequest: {
                          ...action.ordersPaymentRequest,
                          status: OrdersPaymentRequestStatusEnum.userSubmitFailure
                        }
                      })
                    );
                  })
                );
              }
              if (request.transfer) {
                return this.paymentService.authorizeOrderInternalTransfer(request).pipe(
                  switchMap((paymentAuthorizationResponse) =>
                    of(
                      fromActions.authorizeOrdersPaymentSuccess({
                        ordersPaymentRequest: { ...action.ordersPaymentRequest, paymentAuthorizationResponse }
                      })
                    )
                  ),
                  catchError(() => {
                    this.navigateToPayments();
                    return of(
                      fromActions.authorizeOrdersPaymentFailure({
                        ordersPaymentRequest: {
                          ...action.ordersPaymentRequest,
                          status: OrdersPaymentRequestStatusEnum.userSubmitFailure
                        }
                      })
                    );
                  })
                );
              }
              return throwError(() => new Error(this.unknownPaymentType));
            } else {
              return of(
                fromActions.authorizeOrdersPaymentFailure({
                  ordersPaymentRequest: {
                    ...action.ordersPaymentRequest,
                    status: OrdersPaymentRequestStatusEnum.userSubmitFailure
                  }
                })
              );
            }
          })
        )
      )
    )
  );

  confirmAuthorizedOrdersPayment = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.confirmAuthorizedOrdersPayment),
      withLatestFrom(this.store.select(selectIsAccessAppAvailable)),
      switchMap(([action, isAccessAppAvailable]) =>
        this.store.select(fromSelectors.selectOrdersPaymentRequests).pipe(
          take(1),
          switchMap((ordersPaymentRequests) => {
            if (ordersPaymentRequests?.has(action?.ordersPaymentRequest?.uuid)) {
              const request = ordersPaymentRequests.get(action.ordersPaymentRequest.uuid);
              if (request.payment) {
                return this.paymentService.confirmAuthorizedOrdersPayment(request, isAccessAppAvailable).pipe(
                  tap(() => {
                    this.router.navigateByUrl(
                      `/pages/payments/${action.ordersPaymentRequest.payment ? 'recent-payments' : 'recent-transfers'}`
                    );
                  }),
                  switchMap((confirmAuthorizedPaymentResponse) =>
                    of(
                      fromActions.confirmAuthorizedOrdersPaymentSuccess({
                        ordersPaymentRequest: {
                          ...action.ordersPaymentRequest,
                          confirmAuthorizedPaymentResponse,
                          status: confirmAuthorizedPaymentResponse.apiOrderCreationStatus
                        }
                      })
                    )
                  ),
                  catchError(() => {
                    this.navigateToPayments();
                    return of(
                      fromActions.confirmAuthorizedOrdersPaymentFailure({
                        ordersPaymentRequest: {
                          ...action.ordersPaymentRequest,
                          status: OrdersPaymentRequestStatusEnum.userSubmitFailure
                        }
                      })
                    );
                  })
                );
              }
              if (request.transfer) {
                return this.paymentService.confirmAuthorizedOrdersInternalTransfer(request, isAccessAppAvailable).pipe(
                  tap(() => {
                    this.router.navigateByUrl(
                      `/pages/payments/${action.ordersPaymentRequest.payment ? 'recent-payments' : 'recent-transfers'}`
                    );
                  }),
                  switchMap((confirmAuthorizedPaymentResponse) =>
                    of(
                      fromActions.confirmAuthorizedOrdersPaymentSuccess({
                        ordersPaymentRequest: {
                          ...action.ordersPaymentRequest,
                          confirmAuthorizedPaymentResponse,
                          status: confirmAuthorizedPaymentResponse.apiOrderCreationStatus
                        }
                      })
                    )
                  ),
                  catchError(() => {
                    this.navigateToPayments();
                    return of(
                      fromActions.confirmAuthorizedOrdersPaymentFailure({
                        ordersPaymentRequest: {
                          ...action.ordersPaymentRequest,
                          status: OrdersPaymentRequestStatusEnum.userSubmitFailure
                        }
                      })
                    );
                  })
                );
              }

              return throwError(() => new Error(this.unknownPaymentType));
            } else {
              return of(
                fromActions.confirmAuthorizedOrdersPaymentFailure({
                  ordersPaymentRequest: {
                    ...action.ordersPaymentRequest,
                    status: OrdersPaymentRequestStatusEnum.userSubmitFailure
                  }
                })
              );
            }
          })
        )
      )
    )
  );

  createOrdersPaymentAskForExecution$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.createOrdersPaymentAskForExecution),
      tap(() => this.openStatusScreen()),
      switchMap((action) => {
        const ordersPaymentRequest = {
          ...action.ordersPaymentRequest,
          status: OrdersPaymentRequestStatusEnum.readyToSubmit
        };
        return of(fromActions.createOrdersPaymentAskForExecutionSuccess({ ordersPaymentRequest }));
      }),
      catchError(() => of(fromActions.createOrdersPaymentAskForExecutionFailure()))
    )
  );

  createOrdersPaymentSuccessUserHasRead$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.createOrdersPaymentSuccessUserHasRead),
      switchMap((action) =>
        of(fromActions.createOrdersPaymentSuccessUserHasReadSuccess({ ordersPaymentRequest: action.ordersPaymentRequest }))
      )
    )
  );

  createOrdersPaymentCanceled$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.createOrdersPaymentCanceled),
      switchMap((action) =>
        of(fromActions.createOrdersPaymentCanceledSuccess({ ordersPaymentRequest: action.ordersPaymentRequest }))
      )
    )
  );

  loadSwiftCodesIfNotLoadedYet$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.loadPaymentSwiftCodesOnlyIfNotLoadedYet),
      withLatestFrom(this.store.select(fromSelectors.selectSwiftCodes)),
      switchMap(([action, existingCodes]) => {
        if (!action.country || existingCodes.some((code) => code.countryId === action.country)) {
          return EMPTY;
        }
        return of(fromActions.loadPaymentSwiftCodes({ country: action.country }));
      })
    )
  );

  paymentReturnFromDeepLink$ = createEffect(() =>
    this.actions$.pipe(
      ofType(appStartedByDeepLinkClick),
      filter((action) =>
        Object.values(environment.accessApp.deepLinks.paymentConfirmation).some((deeplink) => action.deepLink?.url === deeplink)
      ),
      switchMap(() => of(fromActions.paymentReturnFromDeepLinkSuccess()))
    )
  );

  private readonly unknownPaymentType = 'unknown payment type';

  constructor(
    private readonly actions$: Actions,
    private readonly store: Store,
    private readonly router: Router,
    private readonly paymentService: PaymentService,
    private readonly modalService: ModalControllerService
  ) {}

  async openStatusScreen() {
    const canDismiss = new ReplaySubject<boolean>(1);
    const closed = new EventEmitter();
    const modal = await this.modalService.show({
      component: ValidatePaymentOrderRequestPage,
      componentProps: {
        canDismiss,
        closed
      }
    });
    canDismiss.pipe(takeUntil(this.destroyed$)).subscribe((dismiss) => {
      modal.canDismiss = dismiss;
    });
    closed.pipe(takeUntil(this.destroyed$)).subscribe(() => {
      modal.dismiss();
    });
  }

  private readonly navigateToPayments = () => this.router.navigateByUrl(`/pages/payments`);
}
